
import {
  CheckCircle,
  Download,
  FileText,
  Hammer,
  Home,
  Mail,
  MapPin,
  Phone,
  Printer,
  Shield,
  Star,
  User,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ConfirmationView = ({
  selectedContractor,
  installationData,
  roofMeasurements,
  homeownerDetails,
  formatDate,
  handleDownloadPDF,
  handlePrint,
}) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Installation Confirmed!
          </h1>
          <p className="text-xl text-gray-600">
            Your roofing project has been successfully scheduled
          </p>
        </div>

        {/* Confirmation Details */}
        <div
          className="bg-white rounded-3xl shadow-lg p-8 mb-8 border border-gray-100"
          data-testid="confirmation-summary"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Installation Summary
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Contractor:</span>
                <span className="font-semibold text-gray-900">
                  {selectedContractor.contractor}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Contact Person:</span>
                <span className="font-semibold text-gray-900">
                  {selectedContractor.contactName}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Total Cost:</span>
                <span className="font-semibold text-emerald-600 text-xl">
                  ${selectedContractor.selectedBid.price.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Timeline:</span>
                <span className="font-semibold text-gray-900">
                  {selectedContractor.selectedBid.timeline}
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Preferred Date:</span>
                <span className="font-semibold text-gray-900">
                  {formatDate(installationData.preferredDate)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Preferred Time:</span>
                <span className="font-semibold text-gray-900">
                  {installationData.preferredTime}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Warranty:</span>
                <span className="font-semibold text-gray-900">
                  {selectedContractor.selectedBid.warranty}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Contact Method:</span>
                <span className="font-semibold text-gray-900 capitalize">
                  {installationData.contactMethod}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Roof Measurements for Roofer */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-8 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Home className="w-7 h-7 text-orange-600 mr-3" />
            Roof Measurements for Roofer
          </h3>

          {/* Main Measurements */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border-2 border-orange-200">
              <div className="text-orange-600 text-sm mb-1">
                Total Roof Area
              </div>
              <div className="text-3xl font-bold text-orange-900">
                {roofMeasurements.totalArea.toLocaleString()}
              </div>
              <div className="text-orange-600 text-sm">sq ft</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border-2 border-blue-200">
              <div className="text-blue-600 text-sm mb-1">Pitched Area</div>
              <div className="text-3xl font-bold text-blue-900">
                {roofMeasurements.pitchedArea.toLocaleString()}
              </div>
              <div className="text-blue-600 text-sm">sq ft</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border-2 border-green-200">
              <div className="text-green-600 text-sm mb-1">Flat Area</div>
              <div className="text-3xl font-bold text-green-900">
                {roofMeasurements.flatArea.toLocaleString()}
              </div>
              <div className="text-green-600 text-sm">sq ft</div>
            </div>
          </div>

          {/* Roof Type and Pitch */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-amber-700 font-medium">Roof Type:</span>
                <span className="ml-2 text-amber-900 font-bold">
                  {roofMeasurements.roofType}
                </span>
              </div>
              <div>
                <span className="text-amber-700 font-medium">
                  Roof Pitch (Slope):
                </span>
                <span className="ml-2 text-amber-900 font-bold">
                  {roofMeasurements.pitch}
                </span>
              </div>
            </div>
          </div>

          {/* Linear Measurements */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
              <div className="text-purple-600 text-xs mb-1">
                Starter Shingles
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {roofMeasurements.starter} ft
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg">
              <div className="text-indigo-600 text-xs mb-1">Ridge Caps</div>
              <div className="text-2xl font-bold text-indigo-900">
                {roofMeasurements.caps} ft
              </div>
            </div>
            <div className="bg-cyan-50 border border-cyan-200 p-3 rounded-lg">
              <div className="text-cyan-600 text-xs mb-1">Valley</div>
              <div className="text-2xl font-bold text-cyan-900">
                {roofMeasurements.valley} ft
              </div>
            </div>
            <div className="bg-teal-50 border border-teal-200 p-3 rounded-lg">
              <div className="text-teal-600 text-xs mb-1">Ridges</div>
              <div className="text-2xl font-bold text-teal-900">
                {roofMeasurements.ridges} ft
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
              <div className="text-emerald-600 text-xs mb-1">Drip Edge</div>
              <div className="text-2xl font-bold text-emerald-900">
                {roofMeasurements.dripEdge} ft
              </div>
            </div>
            <div className="bg-lime-50 border border-lime-200 p-3 rounded-lg">
              <div className="text-lime-600 text-xs mb-1">Perimeter</div>
              <div className="text-2xl font-bold text-lime-900">
                {roofMeasurements.perimeter} ft
              </div>
            </div>
          </div>
        </div>

        {/* Homeowner Details */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-8 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <User className="w-7 h-7 text-blue-600 mr-3" />
            Homeowner Details
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Name</div>
                  <div className="font-semibold text-gray-900">
                    {homeownerDetails.name}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="font-semibold text-gray-900">
                    {homeownerDetails.email}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div className="font-semibold text-gray-900">
                    {homeownerDetails.phone}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Property Address</div>
                  <div className="font-semibold text-gray-900">
                    {homeownerDetails.address}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Home className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Property Type</div>
                  <div className="font-semibold text-gray-900">
                    {homeownerDetails.propertyType}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Hammer className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-xs text-gray-500">Project Type</div>
                  <div className="font-semibold text-gray-900">
                    {homeownerDetails.projectType}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-8 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            What Happens Next?
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-blue-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                Contractor Contact
              </h4>
              <p className="text-gray-600">
                {selectedContractor.contactName} will contact you within 24
                hours to confirm details and answer any questions.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-emerald-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                Permits & Prep
              </h4>
              <p className="text-gray-600">
                All necessary permits will be obtained and site preparation
                completed before the start date.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Hammer className="w-8 h-8 text-purple-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                Installation Begins
              </h4>
              <p className="text-gray-600">
                Work begins on your scheduled date with daily progress updates
                throughout the project.
              </p>
            </div>
          </div>
        </div>

        {/* Contractor Card */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-center space-x-6 mb-6">
            <img
              src={selectedContractor.avatar}
              alt={selectedContractor.contactName}
              className="w-20 h-20 rounded-full object-cover border-4 border-gray-100"
            />
            <div>
              <h4 className="text-2xl font-bold text-gray-900">
                {selectedContractor.contractor}
              </h4>
              <p className="text-gray-600 text-lg">
                {selectedContractor.contactName}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="text-lg font-semibold">
                  {selectedContractor.rating}
                </span>
                <span className="text-gray-500">
                  ({selectedContractor.reviews} reviews)
                </span>
                <div className="flex items-center space-x-1 ml-4">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-600 font-medium">
                    Verified Contractor
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900 text-lg">
                  {selectedContractor.phone}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900">
                  {selectedContractor.email}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-gray-400" />
                <span className="text-gray-900">
                  {selectedContractor.address}
                </span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="text-lg font-semibold text-gray-900">
                Credentials:
              </div>
              <div className="flex flex-wrap gap-3">
                <span className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full font-medium">
                  ✓ Licensed
                </span>
                <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-medium">
                  ✓ Insured
                </span>
                <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-medium">
                  ✓ Bonded
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="text-center space-y-6">
          {/* PDF Download Buttons */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={handleDownloadPDF}
              className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:from-orange-700 hover:to-red-700 transition-all duration-300 transform hover:scale-105 flex items-center space-x-3 shadow-lg"
            >
              <Download className="w-6 h-6" />
              <span>Download PDF Report</span>
            </button>
            <button
              onClick={handlePrint}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 flex items-center space-x-3 shadow-lg"
            >
              <Printer className="w-6 h-6" />
              <span>Print Summary</span>
            </button>
          </div>

          <button
            data-testid="confirm-installation-return-dashboard-button"
            onClick={() => navigate("/orders")}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-10 py-4 rounded-2xl font-semibold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:scale-105"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationView;
